// dropManager.ts
import { get } from 'svelte/store';
import { dragItem } from '../../store'; // your existing dragItem store

/**
 * Suggestions for further improvement:
 * - Support for touch events for mobile drag-and-drop.
 * - Add visual feedback for droppable areas when dragging.
 * - Allow custom drop validation logic per droppable.
 * - Expose drag enter/leave events for more control.
 */

// Keep track of active droppable elements
export type Droppable = {
  element: HTMLElement;
  onDrop: (detail: {
    position: {
      x: number;
      y: number;
    };
    item: any;
  }) => void;
};

export const droppables: Droppable[] = [];

export function registerDroppable(el: HTMLElement, onDrop: Droppable['onDrop']) {
  const droppable: Droppable = { element: el, onDrop };
  droppables.push(droppable);
  // Return unregister function (used in $effect cleanup)
  return () => {
    droppables.splice(droppables.indexOf(droppable), 1);
  };
}

// --- One-time setup of global mouseup and touchend listeners ---
let initialized = false;

export function initDropManager() {
  if (initialized) return;
  initialized = true;

  function handleDropEvent(event: MouseEvent | TouchEvent) {
    const els = droppables;

    const dragged = get(dragItem);
    if (!dragged) return;

    let dropped = false;
    let clientX: number, clientY: number;

    if (event instanceof MouseEvent) {
      clientX = event.clientX;
      clientY = event.clientY;
    } else if (event instanceof TouchEvent && event.changedTouches.length > 0) {
      clientX = event.changedTouches[0].clientX;
      clientY = event.changedTouches[0].clientY;
    } else {
      return;
    }

    for (const { element, onDrop } of els) {
      const rect = element.getBoundingClientRect();
      const isOver =
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom;

      if (isOver) {
        onDrop({
          position: {
            x: clientX,
            y: clientY,
          },
          item: dragged,
        });
        dropped = true;
        break;
      }
    }
    if (!dropped) {
      window.dispatchEvent(new CustomEvent('dropfail', { detail: { event, item: dragged } }));
    }
    // The drag is over either way. A drop onto a droppable clears this too;
    // one let go anywhere else used to leave it set until the next drop.
    dragItem.set(null);
  }

  window.addEventListener('mouseup', handleDropEvent);
  window.addEventListener('touchend', handleDropEvent);
}
